import { customFetch } from '@agos/api-client'
import type { GeocodeSuggestion, PlaceDetails } from '@agos/api-client/model'

/**
 * One address, resolved to a point — `FRONTEND.md` §9.52.
 *
 * `useAddressAutocomplete` is for a field somebody is typing into: it debounces, cancels and keeps a
 * suggestion list. Choosing a **saved** address is none of those things — it is one deliberate click
 * on an address the restaurant already holds — so it wants the two calls once, without the machinery.
 *
 * Shared rather than written into the checkout because the manager's builder offers past addresses
 * the same way (§9.14's chips), and that is two modules by §5's rule.
 */
export interface ResolvedPlace {
  lat: number
  lon: number
  formattedAddress: string
}

/**
 * The server's own types rather than a second declaration of them - §9.416.
 *
 * This file declared `place_id` and three of `PlaceDetails`' four fields by hand. The same reasoning
 * as `use-address-autocomplete.ts`: they agreed, and a rename server-side would have moved the
 * generated type, left these alone and passed the build. Declaring **fewer** fields than the server
 * sends is fine and normal - reading three of four is not a bug - but declaring them *separately* is
 * how a rename stops being a compile error.
 *
 * The `?.` and `== null` guards below are kept: the type is the promise, the guard is for the
 * promise being broken.
 */
type RawSuggestion = Partial<GeocodeSuggestion>

type RawDetails = Partial<PlaceDetails>

/**
 * The first match for this address, or `null`.
 *
 * `null` covers every failure the caller treats alike — nothing found, the provider refusing, the key
 * missing — because the answer to all of them is the same: leave the point as it was and let the
 * person correct the text.
 */
export async function geocodeOnce(address: string): Promise<ResolvedPlace | null> {
  const query = address.trim()
  if (query === '') return null
  try {
    const matches = await customFetch<RawSuggestion[]>(
      `/api/v1/geocode/search?q=${encodeURIComponent(query)}`,
    )
    const placeId = Array.isArray(matches) ? matches[0]?.place_id : undefined
    if (typeof placeId !== 'string' || placeId === '') return null

    const details = await customFetch<RawDetails>(
      `/api/v1/geocode/details?place_id=${encodeURIComponent(placeId)}`,
    )
    if (details?.lat == null || details.lon == null) return null
    return {
      lat: details.lat,
      lon: details.lon,
      formattedAddress: details.formatted_address ?? query,
    }
  } catch {
    return null
  }
}
